import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  Loader2,
  Package,
  Search,
  ExternalLink,
  Download,
  Calendar,
  User,
  MapPin,
  Trash2,
} from "lucide-react";
import { format } from "date-fns";
import { deleteAllBookingPackages, fetchBookingPackages } from "@/lib/bookingPackagesApi";
import { downloadProposalInvoiceFile } from "@/lib/proposalsApi";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface BookingPackageListItem {
  id: string;
  status: string;
  created_at: string;
  proposal: {
    id: string;
    title: string;
    main_destination: string;
    clients: {
      first_name: string;
      last_name: string;
    } | null;
  } | null;
  sales_owner: {
    full_name: string;
  } | null;
  operations_owner: {
    full_name: string;
  } | null;
}

const BookingPackagesPage: React.FC = () => {
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [bookingPackages, setBookingPackages] = useState<BookingPackageListItem[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [downloadingInvoiceFor, setDownloadingInvoiceFor] = useState<string | null>(null);
  const { toast } = useToast();

  const canAccess = userRole === "ceo" || userRole === "general_manager" || userRole === "finance_staff" || userRole === "sales_staff";
  const isCeo = userRole === "ceo";

  useEffect(() => {
    if (canAccess) {
      fetchBookingPackagesData();
    }
  }, [canAccess, searchTerm, statusFilter]);

  const fetchBookingPackagesData = async () => {
    try {
      setLoading(true);
      const response = await fetchBookingPackages({
        page: 1,
        size: 2000,
        search: searchTerm,
        status: statusFilter === "all" ? undefined : statusFilter,
      });
      setBookingPackages(response.items || []);
    } catch (error: any) {
      toast({
        title: "Error loading booking packages",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string }> = {
      ready_to_book: { label: "Ready to Book", className: "bg-emerald-100 text-emerald-700 border-emerald-200" },
      booked: { label: "Booked", className: "bg-blue-100 text-blue-700 border-blue-200" },
      completed: { label: "Completed", className: "bg-gray-100 text-gray-700 border-gray-200" },
    };

    const config = statusConfig[status] || { label: status.replace(/_/g, " "), className: "bg-gray-100 text-gray-700" };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const filteredPackages = bookingPackages;

  const handleDeleteAllConfirm = async () => {
    try {
      setDeletingAll(true);
      if (filteredPackages.length === 0) {
        toast({
          title: "No booking packages to delete",
          description: "The list is already empty.",
        });
        setDeleteAllDialogOpen(false);
        return;
      }
      await deleteAllBookingPackages();
      toast({
        title: "All booking packages deleted",
        description: "All booking packages and related services were removed.",
      });
      setDeleteAllDialogOpen(false);
      fetchBookingPackagesData();
    } catch (error: any) {
      toast({
        title: "Error deleting booking packages",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeletingAll(false);
    }
  };

  const handleDownloadProposalInvoice = async (bookingPackageId: string, proposalId?: string | null) => {
    if (!proposalId) {
      toast({
        title: "Invoice not available",
        description: "This booking package has no linked proposal invoice.",
      });
      return;
    }

    try {
      setDownloadingInvoiceFor(bookingPackageId);
      const { blob, fileName } = await downloadProposalInvoiceFile(proposalId);
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = fileName || `proposal-invoice-${proposalId}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error: any) {
      toast({
        title: "Error downloading invoice",
        description: error?.message || "Could not download the proposal invoice.",
        variant: "destructive",
      });
    } finally {
      setDownloadingInvoiceFor(null);
    }
  };

  if (!canAccess) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold">Access Denied</h2>
          <p className="text-muted-foreground">
            Only Sales, Finance team, Managers, and CEO can access Booking Packages.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Package className="h-6 w-6" />
              Booking Packages
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage reservations for approved proposals
            </p>
          </div>
          <div className="flex gap-2" />
        </div>

        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by client, title, or destination..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="ready_to_book">Ready to Book</SelectItem>
                  <SelectItem value="booked">Booked</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>All Booking Packages ({filteredPackages.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : filteredPackages.length === 0 ? (
              <div className="text-center py-12">
                <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No booking packages found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Proposal</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Destination</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Assigned To</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPackages.map((bp) => (
                      <TableRow
                        key={bp.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => navigate(`/booking-package/${bp.id}`)}
                      >
                        <TableCell className="font-medium">
                          {bp.proposal?.title || "Untitled"}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3 text-muted-foreground" />
                            {bp.proposal?.clients
                              ? `${bp.proposal.clients.first_name} ${bp.proposal.clients.last_name}`
                              : "—"}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3 text-muted-foreground" />
                            {bp.proposal?.main_destination || "—"}
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(bp.status)}</TableCell>
                        <TableCell>
                          {bp.sales_owner?.full_name || bp.operations_owner?.full_name || "—"}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            {format(new Date(bp.created_at), "MMM d, yyyy")}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={downloadingInvoiceFor === bp.id}
                            onClick={async (e) => {
                              e.stopPropagation();
                              await handleDownloadProposalInvoice(bp.id, bp.proposal?.id);
                            }}
                            title="Download Proposal Invoice"
                          >
                            {downloadingInvoiceFor === bp.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/booking-package/${bp.id}`);
                            }}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <AlertDialog open={deleteAllDialogOpen} onOpenChange={setDeleteAllDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete all booking packages</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete all booking packages and their related services from this screen. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deletingAll}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteAllConfirm}
                disabled={deletingAll}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deletingAll ? "Deleting..." : "Delete all"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default BookingPackagesPage;
